import { matchPath, useLocation } from "react-router";

import { Goto } from "@/shared/navigation/Goto";

// Each value is the path segment the corresponding tab is mounted at directly
// under `/devices/:deviceId`, e.g. /devices/:deviceId/sessions resolves to
// DeviceTab.Sessions. Keep these strings in sync with the route definitions so
// URL <-> tab matching stays lossless.
export enum DeviceTab {
  Overview = "overview",
  Details = "details",
  Tokens = "tokens",
  Datasets = "datasets",
  Configurations = "configurations",
  Sessions = "sessions",
  Metrics = "metrics",
}

// The tab is the FIRST segment under `/devices/:deviceId`, not the last
// segment of the URL: a tab may own deeper segments of its own. The Metrics
// tab is mounted at `metrics/:dashboardId?` and rewrites the URL to
// `/devices/:deviceId/metrics/:dashboardId` as soon as it resolves a landing
// dashboard, so reading the trailing segment would see the dashboard id; the
// Configurations tab's URL continues with the path of the folder it has open.
// Matched with `end: false` so those trailing segments are allowed but ignored.
const DEVICE_TAB_PATTERN = `${Goto.route.Devices}/:deviceId/:tab`;

function deriveActiveTabFromUrl(path: string | undefined) {
  switch (path) {
    case DeviceTab.Overview:
      return DeviceTab.Overview;
    case DeviceTab.Tokens:
      return DeviceTab.Tokens;
    case DeviceTab.Datasets:
      return DeviceTab.Datasets;
    case DeviceTab.Configurations:
      return DeviceTab.Configurations;
    case DeviceTab.Sessions:
      return DeviceTab.Sessions;
    case DeviceTab.Metrics:
      return DeviceTab.Metrics;
    case DeviceTab.Details:
      return DeviceTab.Details;
    default:
      // Unknown or missing tab segment (e.g. the device root URL) falls through
      // to Datasets, which is treated as the device landing tab.
      return DeviceTab.Datasets;
  }
}

/**
 * The active device-detail tab, derived from the current URL on every render.
 *
 * The URL is the single source of truth: `useLocation` re-renders on each
 * pathname change, and the tab strip navigates through `<Link>`s, so there is
 * no separate tab state to fall out of sync with the address bar — including
 * when a tab redirects within itself (the Metrics tab's landing-dashboard
 * rewrite).
 */
export function useActiveDeviceTab(): DeviceTab {
  const location = useLocation();
  const match = matchPath(
    { path: DEVICE_TAB_PATTERN, end: false },
    location.pathname,
  );

  return deriveActiveTabFromUrl(match?.params.tab);
}
